import { useQueries } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import {
  FiChevronDown, FiChevronRight, FiFilter, FiSearch, FiX
} from 'react-icons/fi';

import APIGateway from '@/api';
import { Loader } from '@/components/loader/loader';
import type { TraceRequestFilters } from '@/hooks/queries/useTraces';
import {
  traceKeys,
  useTraceActionLogsQuery,
  useTraceModelLogsQuery,
  useTraceRequestsByRequestIdQuery,
  useTraceRequestsQuery
} from '@/hooks/queries/useTraces';
import type {
  ActionLog, LLMMessage, MessageChoice,ModelLog, RequestLog, ResponseLog, SortOrder
} from '@/types/logs';
import type { TimelineEntry, TraceEvent } from '@/types/trace';
import { formatISODateTime } from '@/utils/timeFormatter';

import styles from './traceViewer.module.scss';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Safely resolve a value that may be a JSON string or already an object */
function resolveJsonOrObject( value: unknown ): any {
  if ( typeof value === 'string' ) {
    try { return JSON.parse( value ); } catch { return value; }
  }
  return value;
}

/** Normalize a choice from either Chat Completions (`choice.message`) or Responses API
 *  (`role`/`content` directly on choice, with `content` as an array of parts). */
function normalizeChoice( choice: any ): { role: string; content: string; tool_calls?: any[] } {
  const source = choice?.message ?? choice ?? {};
  const role = source.role ?? '';
  let content: string;
  if ( typeof source.content === 'string' ) {
    content = source.content;
  } else if ( Array.isArray( source.content ) ) {
    content = source.content
      .map( ( part: any ) => part?.text ?? ( typeof part === 'string' ? part : JSON.stringify( part ) ) )
      .join( '' );
  } else {
    content = '';
  }
  return { role, content, tool_calls: source.tool_calls };
}

function durationSeconds( start: string, end: string ): string {
  const s = DateTime.fromISO( start );
  const e = DateTime.fromISO( end );
  if ( !s.isValid || !e.isValid ) return '-';
  return e.diff( s, 'seconds' ).seconds.toFixed( 2 ) + 's';
}

function extractUserMessage( req: RequestLog ): string {
  const requestPayload = resolveJsonOrObject( req.meta_data?.request );
  const messages = requestPayload?.messages;
  if ( !Array.isArray( messages ) || messages.length === 0 ) return '-';
  const last = messages[ messages.length - 1 ];
  const text = last?.content?.text ?? last?.content;
  return typeof text === 'string' ? text : JSON.stringify( text );
}

function extractFinalResponse( responses: ResponseLog[] ): string {
  for ( let i = responses.length - 1; i >= 0; i-- ) {
    const responsePayload = resolveJsonOrObject( responses[ i ].meta_data?.response );
    const msgs = responsePayload?.messages;
    if ( !Array.isArray( msgs ) ) continue;
    for ( const m of msgs ) {
      if ( m?.content?.type === 'TextualContent' && m.content.text ) {
        return m.content.text;
      }
    }
  }
  return '-';
}

function truncate( s: string, max: number = 120 ): string {
  return s.length > max ? s.slice( 0, max ) + '...' : s;
}

// ── URL state type for shareable links ──────────────────────────────────────

export type TraceUrlState = {
  traceRequestId: string;
  traceCorrelationId: string;
  traceClientId: string;
  traceSessionId: string;
  traceAgentName: string;
  traceConstellationName: string;
  traceConstellationVersion: string;
  traceDateFrom: string;
  traceDateTo: string;
  traceKeyword: string;
  traceSortOrder: SortOrder | '';
  traceSortBy: string;
  traceOffset: number;
  selectedRequest: string;
};

const SORT_BY_OPTIONS: { value: string; label: string }[] = [
  { value: 'start_time', label: 'Start time' },
  { value: 'end_time', label: 'End time' }
];

// ── Request List Panel ───────────────────────────────────────────────────────

interface RequestListProps {
  agentId: string;
  useLocalZone: boolean;
  initialUrlState?: TraceUrlState;
  onUrlStateChange?: ( state: TraceUrlState ) => void;
  /** When true, hide the built-in filters panel + toggle. Used when an outer
   *  page (e.g. /logs) provides its own filter UI that controls this viewer. */
  hideFiltersPanel?: boolean;
}

function RequestListPanel( { agentId, useLocalZone, initialUrlState, onUrlStateChange, hideFiltersPanel }: RequestListProps ) {
  const [ offset, setOffset ] = useState( initialUrlState?.traceOffset ?? 0 );
  const batchSize = 20;

  // Filter state — all server-side except keyword
  const [ filtersOpen, setFiltersOpen ] = useState( false );
  const [ filterRequestId, setFilterRequestId ] = useState( initialUrlState?.traceRequestId ?? '' );
  const [ filterCorrelationId, setFilterCorrelationId ] = useState( initialUrlState?.traceCorrelationId ?? '' );
  const [ filterClientId, setFilterClientId ] = useState( initialUrlState?.traceClientId ?? '' );
  const [ filterSessionId, setFilterSessionId ] = useState( initialUrlState?.traceSessionId ?? '' );
  const [ filterAgentName, setFilterAgentName ] = useState( initialUrlState?.traceAgentName ?? '' );
  const [ filterConstellationName, setFilterConstellationName ] = useState( initialUrlState?.traceConstellationName ?? '' );
  const [ filterConstellationVersion, setFilterConstellationVersion ] = useState( initialUrlState?.traceConstellationVersion ?? '' );
  const [ filterDateFrom, setFilterDateFrom ] = useState( initialUrlState?.traceDateFrom ?? '' );
  const [ filterDateTo, setFilterDateTo ] = useState( initialUrlState?.traceDateTo ?? '' );
  const [ filterSortOrder, setFilterSortOrder ] = useState<SortOrder | ''>( initialUrlState?.traceSortOrder ?? '' );
  const [ filterSortBy, setFilterSortBy ] = useState( initialUrlState?.traceSortBy ?? '' );
  const [ keyword, setKeyword ] = useState( initialUrlState?.traceKeyword ?? '' );

  // Build server-side filters reactively — any change re-fetches
  const serverFilters = useMemo<TraceRequestFilters>( () => ( {
    agentName: filterAgentName || undefined,
    constellationName: filterConstellationName || undefined,
    constellationVersion: filterConstellationVersion || undefined,
    clientId: filterClientId || undefined,
    sessionId: filterSessionId || undefined,
    requestId: filterRequestId || undefined,
    correlationId: filterCorrelationId || undefined,
    startTime: filterDateFrom ? DateTime.fromISO( filterDateFrom ).toISO()! : undefined,
    endTime: filterDateTo ? DateTime.fromISO( filterDateTo ).toISO()! : undefined,
    sortOrder: filterSortOrder || undefined,
    sortBy: filterSortBy || undefined
  } ), [
    filterAgentName, filterConstellationName, filterConstellationVersion,
    filterClientId, filterSessionId, filterRequestId, filterCorrelationId,
    filterDateFrom, filterDateTo, filterSortOrder, filterSortBy
  ] );

  // Reset to page 1 when filters change
  const filterKey = JSON.stringify( serverFilters );
  const [ prevFilterKey, setPrevFilterKey ] = useState( filterKey );
  if ( filterKey !== prevFilterKey ) {
    setPrevFilterKey( filterKey );
    if ( offset !== 0 ) setOffset( 0 );
  }

  const clearFilters = useCallback( () => {
    setFilterRequestId( '' );
    setFilterCorrelationId( '' );
    setFilterClientId( '' );
    setFilterSessionId( '' );
    setFilterAgentName( '' );
    setFilterConstellationName( '' );
    setFilterConstellationVersion( '' );
    setFilterDateFrom( '' );
    setFilterDateTo( '' );
    setFilterSortOrder( '' );
    setFilterSortBy( '' );
    setKeyword( '' );
    setOffset( 0 );
  }, [] );

  const hasServerFilter = filterRequestId || filterCorrelationId || filterClientId
    || filterSessionId || filterAgentName || filterConstellationName
    || filterConstellationVersion || filterDateFrom || filterDateTo
    || filterSortOrder || filterSortBy;
  const hasAnyFilter = hasServerFilter || keyword;

  const { data, isLoading, isError } = useTraceRequestsQuery( agentId, batchSize, offset, serverFilters );
  const rawRequests = data?.completion_requests ?? [];

  // Client-side keyword filter only
  const requests = useMemo( () => {
    if ( !keyword.trim() ) return rawRequests;
    const kw = keyword.trim().toLowerCase();
    return rawRequests.filter( r => {
      const msg = extractUserMessage( r ).toLowerCase();
      const rid = ( r.request_id ?? r.id ?? '' ).toLowerCase();
      const callId = ( r.meta_data?.call_id ?? '' ).toLowerCase();
      const cid = ( r.correlation_id ?? '' ).toLowerCase();
      const clientId = ( r.client_id ?? '' ).toLowerCase();
      return msg.includes( kw )
        || rid.includes( kw )
        || callId.includes( kw )
        || cid.includes( kw )
        || clientId.includes( kw );
    } );
  }, [ rawRequests, keyword ] );

  // Each row is identified by its `call_id` — the per-invocation key. Multiple
  // request log rows can share the same `request_id` (parent + subagents that
  // propagated it), so selecting by request_id would highlight all of them.
  const [ selectedRowKey, setSelectedRowKey ] = useState<string | null>( initialUrlState?.selectedRequest || null );
  const rowKey = ( r: RequestLog ): string => r.meta_data?.call_id ?? r.id;
  const selectedRequest = requests.find( r => rowKey( r ) === selectedRowKey );

  // Notify parent of state changes for URL sync
  const isInitialMount = useRef( true );
  useEffect( () => {
    if ( isInitialMount.current ) {
      isInitialMount.current = false;
      return;
    }
    onUrlStateChange?.( {
      traceRequestId: filterRequestId,
      traceCorrelationId: filterCorrelationId,
      traceClientId: filterClientId,
      traceSessionId: filterSessionId,
      traceAgentName: filterAgentName,
      traceConstellationName: filterConstellationName,
      traceConstellationVersion: filterConstellationVersion,
      traceDateFrom: filterDateFrom,
      traceDateTo: filterDateTo,
      traceKeyword: keyword,
      traceSortOrder: filterSortOrder,
      traceSortBy: filterSortBy,
      traceOffset: offset,
      selectedRequest: selectedRowKey ?? ''
    } );
  }, [
    filterRequestId, filterCorrelationId, filterClientId, filterSessionId,
    filterAgentName, filterConstellationName, filterConstellationVersion,
    filterDateFrom, filterDateTo, keyword, filterSortOrder, filterSortBy,
    offset, selectedRowKey, onUrlStateChange
  ] );

  return (
    <div className={ styles.traceViewer }>
      <div className={ styles.requestList }>
        <div className={ styles.requestListHeader }>
          <span>Requests</span>
          {!hideFiltersPanel && (
            <button
              type="button"
              className={ `${ styles.filterToggle } ${ hasAnyFilter ? styles.filterToggleActive : '' }` }
              onClick={ () => setFiltersOpen( !filtersOpen ) }
              title="Toggle filters"
            >
              <FiFilter size={ 14 } />
            </button>
          )}
        </div>

        {!hideFiltersPanel && filtersOpen && (
          <div className={ styles.filtersPanel }>
            <div className={ styles.filterRow }>
              <div className={ styles.searchBox }>
                <FiSearch size={ 13 } className={ styles.searchIcon } />
                <input
                  type="text"
                  placeholder="Keyword search..."
                  value={ keyword }
                  onChange={ e => setKeyword( e.target.value ) }
                  className={ styles.filterInput }
                />
              </div>
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>From</label>
              <input
                type="datetime-local"
                value={ filterDateFrom }
                onChange={ e => setFilterDateFrom( e.target.value ) }
                className={ styles.filterInput }
              />
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>To</label>
              <input
                type="datetime-local"
                value={ filterDateTo }
                onChange={ e => setFilterDateTo( e.target.value ) }
                className={ styles.filterInput }
              />
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>Request ID</label>
              <input
                type="text"
                placeholder="e.g. 458af480-..."
                value={ filterRequestId }
                onChange={ e => setFilterRequestId( e.target.value ) }
                className={ styles.filterInput }
              />
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>Correlation ID</label>
              <input
                type="text"
                placeholder="e.g. 817c36ab-..."
                value={ filterCorrelationId }
                onChange={ e => setFilterCorrelationId( e.target.value ) }
                className={ styles.filterInput }
              />
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>Client ID</label>
              <input
                type="text"
                placeholder="e.g. aidp-studio"
                value={ filterClientId }
                onChange={ e => setFilterClientId( e.target.value ) }
                className={ styles.filterInput }
              />
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>Session ID</label>
              <input
                type="text"
                placeholder="Session ID"
                value={ filterSessionId }
                onChange={ e => setFilterSessionId( e.target.value ) }
                className={ styles.filterInput }
              />
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>Agent name</label>
              <input
                type="text"
                placeholder="Agent name"
                value={ filterAgentName }
                onChange={ e => setFilterAgentName( e.target.value ) }
                className={ styles.filterInput }
              />
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>Constellation</label>
              <input
                type="text"
                placeholder="Constellation name"
                value={ filterConstellationName }
                onChange={ e => setFilterConstellationName( e.target.value ) }
                className={ styles.filterInput }
              />
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>Const. ver</label>
              <input
                type="text"
                placeholder="e.g. 1.0.0"
                value={ filterConstellationVersion }
                onChange={ e => setFilterConstellationVersion( e.target.value ) }
                className={ styles.filterInput }
              />
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>Sort by</label>
              <select
                value={ filterSortBy }
                onChange={ e => setFilterSortBy( e.target.value ) }
                className={ styles.filterInput }
              >
                <option value="">Default ({SORT_BY_OPTIONS[0].label})</option>
                {SORT_BY_OPTIONS.map( opt => (
                  <option key={ opt.value } value={ opt.value }>{opt.label}</option>
                ) )}
              </select>
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>Order</label>
              <select
                value={ filterSortOrder }
                onChange={ e => setFilterSortOrder( ( e.target.value || '' ) as SortOrder | '' ) }
                className={ styles.filterInput }
              >
                <option value="">Newest first (default)</option>
                <option value="DESC">Newest first</option>
                <option value="ASC">Oldest first</option>
              </select>
            </div>
            {hasAnyFilter && (
              <div className={ styles.filterActions }>
                <button type="button" className={ styles.filterClear } onClick={ clearFilters }>
                  <FiX size={ 12 } /> Clear all
                </button>
              </div>
            )}
          </div>
        )}

        <div className={ styles.requestItems }>
          {isLoading ? (
            <div className={ styles.loading }>Loading...</div>
          ) : isError ? (
            <p className={ styles.error }>Error loading requests</p>
          ) : requests.length === 0 ? (
            <p className={ styles.empty }>
              {hasAnyFilter ? 'No requests match the current filters.' : 'No requests found. Traces will appear once this agent receives traffic.'}
            </p>
          ) : (
            requests.map( req => {
              const key = rowKey( req );
              const isSelected = key === selectedRowKey;
              const userMsg = extractUserMessage( req );
              return (
                <button
                  key={ req.id }
                  type="button"
                  className={ `${ styles.requestItem } ${ isSelected ? styles.requestItemActive : '' }` }
                  onClick={ () => setSelectedRowKey( key ) }
                >
                  <div className={ styles.requestItemTime }>
                    {formatISODateTime( req.start_time, useLocalZone, 'M/d hh:mm:ss a' )}
                  </div>
                  <div className={ styles.requestItemMessage }>
                    {truncate( userMsg, 80 )}
                  </div>
                </button>
              );
            } )
          )}
        </div>
        <div className={ styles.pagination }>
          <button type="button" disabled={ offset === 0 } onClick={ () => setOffset( Math.max( 0, offset - batchSize ) ) }>
            Prev
          </button>
          <span>Page {Math.floor( offset / batchSize ) + 1}</span>
          <button type="button" disabled={ rawRequests.length < batchSize } onClick={ () => setOffset( offset + batchSize ) }>
            Next
          </button>
        </div>
      </div>

      <div className={ styles.traceDetail }>
        {selectedRequest ? (
          <TraceDetailPanel
            request={ selectedRequest }
            agentId={ agentId }
            useLocalZone={ useLocalZone }
          />
        ) : (
          <div className={ styles.selectPrompt }>Select a request to view its trace</div>
        )}
      </div>
    </div>
  );
}

// ── Trace Detail Panel (Gantt + sequential events) ───────────────────────────

interface TraceDetailPanelProps {
  request: RequestLog;
  agentId: string;
  useLocalZone: boolean;
}

interface SubagentInvocation {
  id: string;
  agent: string;
  requestLog?: RequestLog;
  callId?: string;
  startTime: string;
  endTime: string;
  modelLogs: ModelLog[];
  actionLogs: ActionLog[];
}

function logCallId( log: ModelLog | ActionLog ): string | undefined {
  // Model logs carry the call_id under `requester_metadata.kanpai_call_id`
  // (litellm passes through the tags we set on the request); action logs carry
  // it directly on `meta_data.call_id`.
  const reqMeta = ( log as ModelLog ).requester_metadata;
  if ( reqMeta && typeof reqMeta === 'object' ) {
    const cid = ( reqMeta as Record<string, unknown> ).kanpai_call_id;
    if ( typeof cid === 'string' ) return cid;
  }
  const actionMeta = ( log as ActionLog ).meta_data;
  return ( actionMeta as { call_id?: string } | undefined )?.call_id;
}

function TraceDetailPanel( { request, agentId: _agentId, useLocalZone }: TraceDetailPanelProps ) {
  const requestId = request.request_id ?? request.id;
  const parentCallId = request.meta_data?.call_id;

  // Fetch model + action logs by request_id only — gives us logs from the parent
  // agent AND any subagents that propagated the same request_id. Sibling requests
  // (same request_id, all agents) let us enrich each subagent invocation with its
  // own request log timing and call_id when available.
  const { data: modelData, isLoading: modelsLoading } = useTraceModelLogsQuery( undefined, requestId );
  const { data: actionData, isLoading: actionsLoading } = useTraceActionLogsQuery( undefined, requestId );
  const { data: requestsByIdData, isLoading: siblingRequestsLoading } = useTraceRequestsByRequestIdQuery( requestId );

  const allModelLogs = useMemo( () => modelData?.model_invocations ?? [], [ modelData ] );
  const allActionLogs = useMemo( () => actionData?.agent_actions ?? [], [ actionData ] );
  const siblingRequests = useMemo( () => requestsByIdData?.completion_requests ?? [], [ requestsByIdData ] );

  // Response fetching has to happen BEFORE subagent discovery because the
  // parent's responses contribute to the upper bound of the parent's effective
  // window (a passthrough parent's only "post-LLM" activity is dispatching its
  // response). To avoid a cyclic dep on `subagents`, we derive call_ids from
  // siblingRequests directly. React Query dedupes by queryKey, so re-using a
  // call_id across renders has no extra network cost.
  const callIds = useMemo( () => {
    const ids: string[] = [];
    if ( parentCallId ) ids.push( parentCallId );
    siblingRequests.forEach( r => {
      const cid = r.meta_data?.call_id;
      if ( cid && cid !== parentCallId ) ids.push( cid );
    } );
    return ids;
  }, [ parentCallId, siblingRequests ] );

  // `combine` keeps the derived value referentially stable across renders so
  // downstream useMemos don't re-fire on every render.
  const { responsesByCallId, responsesLoading } = useQueries( {
    queries: callIds.map( cid => ( {
      queryKey: traceKeys.traceResponses( cid ),
      queryFn: () => APIGateway.logs.responseLogsGet( cid ),
      enabled: !!cid,
      staleTime: 60000
    } ) ),
    combine: ( results ) => {
      const map = new Map<string, ResponseLog[]>();
      results.forEach( ( r, i ) => {
        map.set( callIds[ i ], r.data?.completion_responses ?? [] );
      } );
      return {
        responsesByCallId: map,
        responsesLoading: results.some( r => r.isLoading )
      };
    }
  } );

  // Discover subagent invocations.
  //
  // RequestLogs themselves are point-in-time (start_time === end_time = the
  // instant the request was received), so we can't use the parent RequestLog's
  // end_time as the window upper bound. Instead we compute the parent's
  // effective window from its OWN model logs / action logs / responses (each
  // attributed by call_id), then require a subagent's entire activity span to
  // sit inside that window — anything starting before, starting after, or
  // running past the parent's end belongs to a different transaction (or to a
  // parent above this one in the call tree) that just happens to share the
  // same request_id.
  //
  // Attribution: every RequestLog carries `meta_data.call_id`, and every
  // model/action log carries the same `call_id` on the LLM call that produced
  // it. We attribute each log directly via that call_id. Logs whose call_id
  // doesn't match any in-scope RequestLog are dropped — they belong to an
  // out-of-scope agent (typically a parent above the inspected request).
  const { subagents, parentModelLogs, parentActionLogs } = useMemo( () => {
    const parentStartMs = DateTime.fromISO( request.start_time ).toMillis();

    // Parent's own logs: matched directly by parent's call_id, independent of
    // subagent discovery. Used both as the returned parent buckets and to bound
    // the parent's effective window.
    const parentOwnModelLogs = parentCallId
      ? allModelLogs.filter( ml => logCallId( ml ) === parentCallId )
      : [];
    const parentOwnActionLogs = parentCallId
      ? allActionLogs.filter( al => logCallId( al ) === parentCallId )
      : [];
    const parentResponses = parentCallId ? ( responsesByCallId.get( parentCallId ) ?? [] ) : [];

    // Parent's effective end = latest of its own activity (model logs, action
    // logs, responses, request.end_time). Until the parent's responses load
    // this falls back to the latest log timestamp, which still gives a usable
    // window because parent model logs typically span the whole invocation.
    const parentEndCandidates = [
      ...parentOwnModelLogs.map( m => m.end_time ),
      ...parentOwnActionLogs.map( a => a.end_time ),
      ...parentResponses.map( r => r.end_time || r.start_time ),
      request.end_time
    ].filter( Boolean ) as string[];
    const parentEndMs = parentEndCandidates.length
      ? Math.max( ...parentEndCandidates.map( t => DateTime.fromISO( t ).toMillis() ).filter( t => !isNaN( t ) ) )
      : parentStartMs;

    // Build a call_id → request map across all siblings (log attribution by
    // call_id still works for unique invocations regardless of timing).
    const requestByCallId = new Map<string, RequestLog>();
    for ( const c of siblingRequests ) {
      const cid = c.meta_data?.call_id;
      if ( cid ) requestByCallId.set( cid, c );
    }
    if ( parentCallId && !requestByCallId.has( parentCallId ) ) {
      requestByCallId.set( parentCallId, request );
    }

    const findOwner = ( log: ModelLog | ActionLog ): RequestLog | undefined => {
      const cid = logCallId( log );
      if ( !cid ) return undefined;
      return requestByCallId.get( cid );
    };

    const logsByOwnerId = new Map<string, { models: ModelLog[]; actions: ActionLog[] }>();
    const ensure = ( id: string ) => {
      if ( !logsByOwnerId.has( id ) ) logsByOwnerId.set( id, { models: [], actions: [] } );
      return logsByOwnerId.get( id )!;
    };

    // Orphan attribution: logs without an owner get attributed to the parent so
    // they aren't dropped. If the log has no call_id at all, attribute
    // unconditionally — there's no way to place it anywhere else. If the log
    // has a call_id that simply doesn't match any sibling RequestLog, only
    // attribute when start_time falls inside the parent's effective window
    // (otherwise it likely belongs to a different transaction sharing the
    // request_id).
    const isInParentWindow = ( log: ModelLog | ActionLog ): boolean => {
      const startMs = DateTime.fromISO( log.start_time ).toMillis();
      if ( isNaN( startMs ) ) return false;
      return startMs >= parentStartMs && startMs <= parentEndMs;
    };
    const shouldAttributeOrphan = ( log: ModelLog | ActionLog ): boolean =>
      !logCallId( log ) || isInParentWindow( log );

    allModelLogs.forEach( ml => {
      const owner = findOwner( ml );
      if ( owner ) ensure( owner.id ).models.push( ml );
      else if ( shouldAttributeOrphan( ml ) ) parentOwnModelLogs.push( ml );
    } );
    allActionLogs.forEach( al => {
      const owner = findOwner( al );
      if ( owner ) ensure( owner.id ).actions.push( al );
      else if ( shouldAttributeOrphan( al ) ) parentOwnActionLogs.push( al );
    } );

    // Build subagent invocations from siblings other than the parent, keeping
    // only those whose entire activity span sits inside the parent's window.
    const invocations: SubagentInvocation[] = siblingRequests
      .filter( r => r.id !== request.id )
      .flatMap( r => {
        const startMs = DateTime.fromISO( r.start_time ).toMillis();
        if ( isNaN( startMs ) ) return [];

        const bucket = logsByOwnerId.get( r.id ) ?? { models: [], actions: [] };
        const endCandidates = [
          ...bucket.models.map( m => m.end_time ),
          ...bucket.actions.map( ac => ac.end_time ),
          r.end_time
        ].filter( Boolean ) as string[];
        const endMs = endCandidates.length
          ? Math.max( ...endCandidates.map( t => DateTime.fromISO( t ).toMillis() ).filter( t => !isNaN( t ) ) )
          : startMs;

        // Require the entire subagent activity to fit inside the parent's window.
        if ( startMs < parentStartMs || startMs > parentEndMs ) return [];
        if ( isFinite( endMs ) && endMs > parentEndMs ) return [];

        const endTime = isFinite( endMs )
          ? DateTime.fromMillis( endMs ).toISO()!
          : r.start_time;
        return [ {
          id: r.id,
          agent: r.agent_name ?? r.agent_id ?? 'subagent',
          requestLog: r,
          callId: r.meta_data?.call_id,
          startTime: r.start_time,
          endTime,
          modelLogs: bucket.models,
          actionLogs: bucket.actions
        } ];
      } )
      .sort( ( x, y ) => DateTime.fromISO( x.startTime ).toMillis() - DateTime.fromISO( y.startTime ).toMillis()
      );

    return {
      subagents: invocations,
      parentModelLogs: parentOwnModelLogs,
      parentActionLogs: parentOwnActionLogs
    };
  }, [ siblingRequests, allModelLogs, allActionLogs, request, parentCallId, responsesByCallId ] );

  const responses = parentCallId ? ( responsesByCallId.get( parentCallId ) ?? [] ) : [];
  const isLoading = modelsLoading || actionsLoading || responsesLoading || siblingRequestsLoading;

  const userMessage = extractUserMessage( request );
  const finalResponse = extractFinalResponse( responses );

  // Only the in-scope model logs: parent's own + each discovered subagent's.
  // `allModelLogs` is the raw fetch by request_id, which can include logs from
  // a higher-level parent when this view is a subagent — those are filtered out
  // during attribution and must stay out of the topline metrics + duration.
  const scopedModelLogs = useMemo( () => [
    ...parentModelLogs,
    ...subagents.flatMap( s => s.modelLogs )
  ], [ parentModelLogs, subagents ] );

  // Compute the full request duration: from request start to the last response end
  // (covering all subagent activity as well).
  const requestEndTime = useMemo( () => {
    const allEndTimes = [
      request.end_time || request.start_time,
      ...subagents.map( s => s.endTime ),
      ...Array.from( responsesByCallId.values() ).flat().map( r => r.end_time || r.start_time ).filter( Boolean ) as string[],
      ...scopedModelLogs.map( m => m.end_time ).filter( Boolean )
    ].map( t => DateTime.fromISO( t ).toMillis() ).filter( t => !isNaN( t ) );
    if ( allEndTimes.length === 0 ) return request.end_time || request.start_time;
    return DateTime.fromMillis( Math.max( ...allEndTimes ) ).toISO()!;
  }, [ request, subagents, responsesByCallId, scopedModelLogs ] );

  // Build timeline entries for Gantt: parent rows first, then each subagent group.
  const timelineEntries = useMemo( (): TimelineEntry[] => {
    const buildOwnedEntries = (
      models: ModelLog[],
      actions: ActionLog[],
      ownerCallId: string | undefined,
      depth: number,
      idPrefix: string
    ): TimelineEntry[] => {
      const owned: TimelineEntry[] = [];

      models.forEach( ml => {
        owned.push( {
          id: `${ idPrefix }-model-${ ml.id }`,
          kind: 'model',
          label: `LLM: ${ ml.model }`,
          startTime: ml.start_time,
          endTime: ml.end_time,
          depth
        } );
      } );

      actions.forEach( al => {
        owned.push( {
          id: `${ idPrefix }-action-${ al.id }`,
          kind: 'action',
          label: 'Action',
          startTime: al.start_time,
          endTime: al.end_time,
          depth
        } );
      } );

      const ownerResponses = ownerCallId ? ( responsesByCallId.get( ownerCallId ) ?? [] ) : [];
      const responsesWithTime = ownerResponses.filter( r => r.start_time );
      if ( responsesWithTime.length > 0 ) {
        const startTimes = responsesWithTime.map( r => DateTime.fromISO( r.start_time! ).toMillis() );
        const endTimes = responsesWithTime.map( r => DateTime.fromISO( r.end_time || r.start_time! ).toMillis() );
        owned.push( {
          id: `${ idPrefix }-resp-combined`,
          kind: 'response',
          label: `Responses (${ responsesWithTime.length })`,
          startTime: DateTime.fromMillis( Math.min( ...startTimes ) ).toISO()!,
          endTime: DateTime.fromMillis( Math.max( ...endTimes ) ).toISO()!,
          segments: responsesWithTime.map( ( r, i ) => ( {
            id: `${ idPrefix }-resp-seg-${ i }`,
            startTime: r.start_time!,
            endTime: r.end_time || r.start_time!
          } ) ),
          depth
        } );
      }

      // Keep child items sorted by start time
      owned.sort( ( a, b ) => DateTime.fromISO( a.startTime ).toMillis() - DateTime.fromISO( b.startTime ).toMillis()
      );
      return owned;
    };

    // Build the non-user-request rows first so we can anchor the User Request
    // row to the earliest item — orphan logs can predate request.start_time, and
    // the User Request bar should always cover the full span.
    const otherEntries: TimelineEntry[] = [];

    otherEntries.push( ...buildOwnedEntries(
      parentModelLogs, parentActionLogs, parentCallId, 0, `parent-${ request.id }`
    ) );

    // Each subagent: header row at depth 1, its items at depth 2.
    // Extend the subagent's endTime to cover its responses too (they arrive
    // after the model/action logs and aren't known when `subagents` is built).
    subagents.forEach( sub => {
      const subResponses = sub.callId ? ( responsesByCallId.get( sub.callId ) ?? [] ) : [];
      const endCandidates = [
        sub.endTime,
        ...subResponses.map( r => r.end_time || r.start_time )
      ].filter( Boolean ) as string[];
      const endMs = Math.max( ...endCandidates.map( t => DateTime.fromISO( t ).toMillis() ).filter( t => !isNaN( t ) ) );
      const effectiveEnd = isFinite( endMs ) ? DateTime.fromMillis( endMs ).toISO()! : sub.endTime;
      otherEntries.push( {
        id: `sub-${ sub.id }`,
        kind: 'subagent',
        label: `Subagent: ${ sub.agent }`,
        startTime: sub.startTime,
        endTime: effectiveEnd,
        depth: 1
      } );
      otherEntries.push( ...buildOwnedEntries(
        sub.modelLogs, sub.actionLogs, sub.callId, 2, `sub-${ sub.id }`
      ) );
    } );

    const requestStartMs = DateTime.fromISO( request.start_time ).toMillis();
    const earliestStartMs = otherEntries.reduce( ( earliest, e ) => {
      const ms = DateTime.fromISO( e.startTime ).toMillis();
      return !isNaN( ms ) && ms < earliest ? ms : earliest;
    }, isNaN( requestStartMs ) ? Infinity : requestStartMs );
    const userRequestStart = isFinite( earliestStartMs )
      ? DateTime.fromMillis( earliestStartMs ).toISO()!
      : request.start_time;

    return [
      {
        id: `req-${ request.id }`,
        kind: 'request',
        label: 'User Request',
        startTime: userRequestStart,
        endTime: requestEndTime,
        depth: 0
      },
      ...otherEntries
    ];
  }, [
    request, requestEndTime, parentCallId, parentModelLogs, parentActionLogs,
    responsesByCallId, subagents
  ] );

  // Build sequential trace events: parent group, then each subagent group.
  const traceEvents = useMemo( (): TraceEvent[] => {
    const buildOwnedEvents = (
      models: ModelLog[],
      actions: ActionLog[],
      ownerCallId: string | undefined,
      depth: number
    ): TraceEvent[] => {
      const owned: TraceEvent[] = [];

      models.forEach( ml => {
        const hasToolCalls = ml.choices?.some(
          ( c: MessageChoice ) => ( normalizeChoice( c ).tool_calls?.length ?? 0 ) > 0
        );
        owned.push( {
          id: `evt-model-${ ml.id }`,
          kind: 'llm_call',
          timestamp: ml.start_time,
          endTimestamp: ml.end_time,
          title: `LLM Call — ${ ml.model }`,
          detail: ml,
          depth
        } );
        if ( hasToolCalls ) {
          const toolCalls = ml.choices?.flatMap(
            ( c: MessageChoice ) => normalizeChoice( c ).tool_calls ?? []
          ) ?? [];
          toolCalls.forEach( ( tc: any, idx: number ) => {
            owned.push( {
              id: `evt-tool-${ ml.id }-${ idx }`,
              kind: 'tool_call',
              timestamp: ml.end_time,
              title: `Tool Call — ${ tc.function?.name ?? 'unknown' }`,
              detail: tc,
              depth
            } );
          } );
        }
      } );

      actions.forEach( al => {
        const actionLabel = al.string_input?.[ 0 ]
          || ( al.meta_data?.user_message as string )
          || 'Agent Action';
        owned.push( {
          id: `evt-action-${ al.id }`,
          kind: 'action',
          timestamp: al.start_time,
          endTimestamp: al.end_time,
          title: 'Action',
          detail: {
            label: actionLabel,
            agent: ( al as any ).agent ?? ( al as any ).agent_name,
            correlationId: al.correlation_id,
            meta: al.meta_data
          },
          depth
        } );
      } );

      const ownerResponses = ownerCallId ? ( responsesByCallId.get( ownerCallId ) ?? [] ) : [];
      ownerResponses.forEach( ( rl, idx ) => {
        const responsePayload = resolveJsonOrObject( rl.meta_data?.response );
        const msgs = responsePayload?.messages ?? [];
        const contentParts: string[] = [];
        let responseType = 'Response';

        for ( const m of msgs ) {
          const ct = m?.content?.type;
          if ( ct === 'TextualContent' && m.content.text ) {
            contentParts.push( m.content.text );
            responseType = 'Agent Response';
          } else if ( ct === 'TypingAction' ) {
            responseType = 'Typing Indicator';
          } else if ( m?.content ) {
            contentParts.push( typeof m.content === 'string' ? m.content : JSON.stringify( m.content ) );
          }
        }

        owned.push( {
          id: `evt-resp-${ rl.id }`,
          kind: 'response',
          timestamp: rl.start_time || rl.end_time || request.start_time,
          title: `${ responseType } (${ idx + 1 }/${ ownerResponses.length })`,
          detail: contentParts.length > 0 ? contentParts.join( '\n' ) : `[${ responseType }]`,
          depth
        } );
      } );

      owned.sort( ( a, b ) => DateTime.fromISO( a.timestamp ).toMillis() - DateTime.fromISO( b.timestamp ).toMillis()
      );
      return owned;
    };

    // Group events into top-level "slots", each anchored by a start time.
    // The parent's user_message and each of its own llm/tool/action/response
    // events are one-event slots. Each subagent is a multi-event slot
    // (header + its children, children stay attached to the header in their
    // own chronological order). We then sort slots by start time so subagent
    // groups interleave with parent events on the timeline, without splitting
    // a subagent's children away from its header.
    interface Slot { startTime: string; events: TraceEvent[] }
    const otherSlots: Slot[] = [];

    buildOwnedEvents( parentModelLogs, parentActionLogs, parentCallId, 0 ).forEach( e => {
      otherSlots.push( { startTime: e.timestamp, events: [ e ] } );
    } );

    subagents.forEach( sub => {
      const subInput = sub.requestLog ? extractUserMessage( sub.requestLog ) : undefined;
      const subResponses = sub.callId ? ( responsesByCallId.get( sub.callId ) ?? [] ) : [];
      const subFinalResponse = extractFinalResponse( subResponses );
      const endCandidates = [
        sub.endTime,
        ...subResponses.map( r => r.end_time || r.start_time )
      ].filter( Boolean ) as string[];
      const endMs = Math.max( ...endCandidates.map( t => DateTime.fromISO( t ).toMillis() ).filter( t => !isNaN( t ) ) );
      const effectiveEnd = isFinite( endMs ) ? DateTime.fromMillis( endMs ).toISO()! : sub.endTime;
      const header: TraceEvent = {
        id: `evt-sub-${ sub.id }`,
        kind: 'subagent',
        timestamp: sub.startTime,
        endTimestamp: effectiveEnd,
        title: `Subagent Call — ${ sub.agent }`,
        detail: {
          agent: sub.agent,
          requestId: sub.requestLog?.request_id,
          callId: sub.callId,
          correlationId: sub.requestLog?.correlation_id,
          startTime: sub.startTime,
          endTime: effectiveEnd,
          fromRequestLog: !!sub.requestLog,
          input: subInput,
          finalResponse: subFinalResponse !== '-' ? subFinalResponse : undefined,
          responses: subResponses
        },
        depth: 1
      };
      const children = buildOwnedEvents( sub.modelLogs, sub.actionLogs, sub.callId, 2 );
      otherSlots.push( { startTime: sub.startTime, events: [ header, ...children ] } );
    } );

    otherSlots.sort( ( a, b ) => DateTime.fromISO( a.startTime ).toMillis() - DateTime.fromISO( b.startTime ).toMillis()
    );

    // Anchor the User Message to the earliest start across all slots (and the
    // request itself) so it always sorts first — orphan logs can predate
    // request.start_time, and the user message should always lead the list.
    const requestStartMs = DateTime.fromISO( request.start_time ).toMillis();
    const earliestStartMs = otherSlots.reduce( ( earliest, s ) => {
      const ms = DateTime.fromISO( s.startTime ).toMillis();
      return !isNaN( ms ) && ms < earliest ? ms : earliest;
    }, isNaN( requestStartMs ) ? Infinity : requestStartMs );
    const userMessageStart = isFinite( earliestStartMs )
      ? DateTime.fromMillis( earliestStartMs ).toISO()!
      : request.start_time;

    const userMessageEvent: TraceEvent = {
      id: `evt-req-${ request.id }`,
      kind: 'user_message',
      timestamp: userMessageStart,
      title: 'User Message',
      detail: userMessage,
      depth: 0
    };

    return [ userMessageEvent, ...otherSlots.flatMap( s => s.events ) ];
  }, [
    request, parentCallId, parentModelLogs, parentActionLogs,
    subagents, responsesByCallId, userMessage
  ] );

  // Topline metrics: only in-scope model logs (parent + its subagents).
  const totalPromptTokens = scopedModelLogs.reduce( ( sum, ml ) => sum + ( ml.prompt_tokens || 0 ), 0 );
  const totalCompletionTokens = scopedModelLogs.reduce( ( sum, ml ) => sum + ( ml.completion_tokens || 0 ), 0 );
  const totalCost = scopedModelLogs.reduce( ( sum, ml ) => sum + ( ml.cost || 0 ), 0 );
  const totalDuration = durationSeconds( request.start_time, requestEndTime );

  if ( isLoading ) {
    return <div className={ styles.loading }>Loading trace...</div>;
  }

  return (
    <div className={ styles.detailContainer }>
      <div className={ styles.detailHeader }>
        <div className={ styles.detailMeta }>
          <span className={ styles.metaLabel }>Request ID:</span>
          <span className={ styles.metaValue }>{ requestId }</span>
        </div>
        <div className={ styles.detailMeta }>
          <span className={ styles.metaLabel }>Time:</span>
          <span className={ styles.metaValue }>
            {formatISODateTime( request.start_time, useLocalZone, 'M/d/yyyy hh:mm:ss a' )}
          </span>
        </div>
        <div className={ styles.detailSummary }>
          <div><strong>Input:</strong> {truncate( userMessage, 200 )}</div>
          <div><strong>Output:</strong> {truncate( finalResponse, 200 )}</div>
        </div>
        <div className={ styles.toplineMetrics }>
          <div className={ styles.metricCard }>
            <div className={ styles.metricValue }>{ totalPromptTokens.toLocaleString() }</div>
            <div className={ styles.metricLabel }>Prompt Tokens</div>
          </div>
          <div className={ styles.metricCard }>
            <div className={ styles.metricValue }>{ totalCompletionTokens.toLocaleString() }</div>
            <div className={ styles.metricLabel }>Completion Tokens</div>
          </div>
          <div className={ styles.metricCard }>
            <div className={ styles.metricValue }>${ totalCost.toFixed( 4 ) }</div>
            <div className={ styles.metricLabel }>Cost</div>
          </div>
          <div className={ styles.metricCard }>
            <div className={ styles.metricValue }>{ totalDuration }</div>
            <div className={ styles.metricLabel }>Total Time</div>
          </div>
          <div className={ styles.metricCard }>
            <div className={ styles.metricValue }>{ scopedModelLogs.length }</div>
            <div className={ styles.metricLabel }>LLM Calls</div>
          </div>
          {subagents.length > 0 && (
            <div className={ styles.metricCard }>
              <div className={ styles.metricValue }>{ subagents.length }</div>
              <div className={ styles.metricLabel }>Subagents</div>
            </div>
          )}
        </div>
      </div>

      {/* Gantt Timeline */}
      <div className={ styles.ganttSection }>
        <div className={ styles.sectionTitle }>Timeline</div>
        <GanttChart entries={ timelineEntries } useLocalZone={ useLocalZone } />
      </div>

      {/* Sequential Events */}
      <div className={ styles.eventsSection }>
        <div className={ styles.sectionTitle }>Trace Events</div>
        <div className={ styles.eventsList }>
          {traceEvents.map( ( evt ) => (
            <TraceEventCard key={ evt.id } event={ evt } useLocalZone={ useLocalZone } />
          ) )}
        </div>
      </div>
    </div>
  );
}

// ── Gantt Chart ──────────────────────────────────────────────────────────────

interface GanttChartProps {
  entries: TimelineEntry[];
  useLocalZone: boolean;
}

function GanttChart( { entries, useLocalZone }: GanttChartProps ) {
  if ( entries.length === 0 ) return <p className={ styles.empty }>No timeline data</p>;

  const allTimes = entries.flatMap( e => [
    DateTime.fromISO( e.startTime ).toMillis(),
    DateTime.fromISO( e.endTime ).toMillis()
  ] ).filter( t => !isNaN( t ) );

  const minTime = Math.min( ...allTimes );
  const maxTime = Math.max( ...allTimes );
  const totalSpan = maxTime - minTime || 1;

  const kindColors: Record<string, string> = {
    request: '#2563eb',
    model: '#7c3aed',
    action: '#059669',
    response: '#d97706',
    subagent: '#0ea5e9'
  };

  return (
    <div className={ styles.gantt }>
      {entries.map( entry => {
        const color = kindColors[ entry.kind ] || '#6b7280';
        const bars = entry.segments && entry.segments.length > 0
          ? entry.segments
          : [ { id: entry.id, startTime: entry.startTime, endTime: entry.endTime } ];
        const depth = entry.depth ?? 0;

        return (
          <div key={ entry.id } className={ styles.ganttRow }>
            <div className={ styles.ganttLabel }>
              {depth > 0 && (
                <span className={ styles.ganttIndentMarker }>{ '↳ '.repeat( Math.min( depth, 2 ) ) }</span>
              )}
              {entry.label}
            </div>
            <div className={ styles.ganttTrack }>
              {bars.map( seg => {
                const segStartMs = DateTime.fromISO( seg.startTime ).toMillis();
                const segEndMs = DateTime.fromISO( seg.endTime ).toMillis();
                const leftPct = Math.min( ( ( segStartMs - minTime ) / totalSpan ) * 100, 99 );
                const rawWidthPct = ( ( segEndMs - segStartMs ) / totalSpan ) * 100;
                // Point-in-time events (start===end) get a fixed pixel width so they're visible
                const isPointInTime = rawWidthPct < 1;

                return (
                  <div
                    key={ seg.id }
                    className={ styles.ganttBar }
                    style={ {
                      left: `${ leftPct }%`,
                      width: isPointInTime ? '6px' : `${ rawWidthPct }%`,
                      backgroundColor: color
                    } }
                    title={ `${ formatISODateTime( seg.startTime, useLocalZone, 'hh:mm:ss.SSS a' ) } — ${ durationSeconds( seg.startTime, seg.endTime ) }` }
                  />
                );
              } )}
            </div>
            <div className={ styles.ganttDuration }>
              {durationSeconds( entry.startTime, entry.endTime )}
            </div>
          </div>
        );
      } )}
      <div className={ styles.ganttLegend }>
        {Object.entries( kindColors ).map( ( [ kind, color ] ) => (
          <span key={ kind } className={ styles.legendItem }>
            <span className={ styles.legendDot } style={ { backgroundColor: color } } />
            {kind.charAt( 0 ).toUpperCase() + kind.slice( 1 )}
          </span>
        ) )}
      </div>
    </div>
  );
}

// ── Trace Event Card ─────────────────────────────────────────────────────────

interface TraceEventCardProps {
  event: TraceEvent;
  useLocalZone: boolean;
}

function TraceEventCard( { event, useLocalZone }: TraceEventCardProps ) {
  const [ expanded, setExpanded ] = useState( false );

  const kindIcons: Record<string, string> = {
    user_message: 'U',
    llm_call: 'L',
    tool_call: 'T',
    action: 'A',
    response: 'R',
    subagent: 'S'
  };

  const kindAccents: Record<string, string> = {
    user_message: '#2563eb',
    llm_call: '#7c3aed',
    tool_call: '#059669',
    action: '#059669',
    response: '#d97706',
    subagent: '#0ea5e9'
  };

  const renderDetail = () => {
    if ( event.kind === 'user_message' || event.kind === 'response' ) {
      return <pre className={ styles.eventPre }>{ typeof event.detail === 'string' ? event.detail : JSON.stringify( event.detail, null, 2 ) }</pre>;
    }

    if ( event.kind === 'llm_call' ) {
      const ml = event.detail as ModelLog;
      return (
        <div className={ styles.llmDetail }>
          <div className={ styles.llmMeta }>
            <span>Model: {ml.model}</span>
            <span>Prompt tokens: {ml.prompt_tokens}</span>
            <span>Completion tokens: {ml.completion_tokens}</span>
            {ml.cost !== undefined && <span>Cost: ${ml.cost.toFixed( 6 )}</span>}
            <span>Duration: {durationSeconds( ml.start_time, ml.end_time )}</span>
          </div>
          <div className={ styles.llmMessages }>
            <div className={ styles.messagesTitle }>Messages</div>
            {renderLLMMessages( ml.messages )}
          </div>
          {ml.choices && ml.choices.length > 0 && (
            <div className={ styles.llmMessages }>
              <div className={ styles.messagesTitle }>Response</div>
              {ml.choices.map( ( choice: MessageChoice, i: number ) => {
                const normalized = normalizeChoice( choice );
                return (
                  <div key={ i } className={ styles.messageItem }>
                    <span className={ styles.messageRole }>{normalized.role}</span>
                    <pre className={ styles.messageContent }>
                      {normalized.content || ( normalized.tool_calls ? JSON.stringify( normalized.tool_calls, null, 2 ) : '-' )}
                    </pre>
                  </div>
                );
              } )}
            </div>
          )}
        </div>
      );
    }

    if ( event.kind === 'action' ) {
      const act = event.detail as any;
      return (
        <div className={ styles.toolDetail }>
          {act.label && <div><strong>Action:</strong> {act.label}</div>}
          {act.agent && <div><strong>Agent:</strong> {act.agent}</div>}
          {act.meta && <pre className={ styles.eventPre }>{JSON.stringify( act.meta, null, 2 )}</pre>}
        </div>
      );
    }

    if ( event.kind === 'tool_call' ) {
      const tc = event.detail as any;
      return (
        <div className={ styles.toolDetail }>
          <div><strong>Function:</strong> {tc.function?.name}</div>
          <pre className={ styles.eventPre }>{tc.function?.arguments || JSON.stringify( tc, null, 2 )}</pre>
        </div>
      );
    }

    if ( event.kind === 'subagent' ) {
      const s = event.detail as any;
      const responses: ResponseLog[] = s.responses ?? [];
      return (
        <div className={ styles.toolDetail }>
          {s.agent && <div><strong>Agent:</strong> {s.agent}</div>}
          {s.requestId && <div><strong>Request ID:</strong> {s.requestId}</div>}
          {s.callId && <div><strong>Call ID:</strong> {s.callId}</div>}
          {s.correlationId && <div><strong>Correlation ID:</strong> {s.correlationId}</div>}
          {s.startTime && s.endTime && (
            <div><strong>Duration:</strong> {durationSeconds( s.startTime, s.endTime )}</div>
          )}
          {!s.fromRequestLog && (
            <div className={ styles.eventPre }>
              No matching request log — timing derived from model/action logs.
              { ' Subagent responses cannot be fetched without a call_id.' }
            </div>
          )}
          {s.input && (
            <div className={ styles.llmMessages }>
              <div className={ styles.messagesTitle }>Request</div>
              <pre className={ styles.messageContent }>{s.input}</pre>
            </div>
          )}
          {responses.length > 0 && (
            <div className={ styles.llmMessages }>
              <div className={ styles.messagesTitle }>Responses ({responses.length})</div>
              {responses.map( ( rl ) => {
                const responsePayload = resolveJsonOrObject( rl.meta_data?.response );
                const msgs = responsePayload?.messages ?? [];
                const parts: string[] = [];
                for ( const m of msgs ) {
                  const ct = m?.content?.type;
                  if ( ct === 'TextualContent' && m.content.text ) parts.push( m.content.text );
                  else if ( m?.content ) parts.push( typeof m.content === 'string' ? m.content : JSON.stringify( m.content ) );
                }
                return (
                  <pre key={ rl.id } className={ styles.messageContent }>
                    {parts.length > 0 ? parts.join( '\n' ) : JSON.stringify( rl.meta_data?.response ?? rl, null, 2 )}
                  </pre>
                );
              } )}
            </div>
          )}
        </div>
      );
    }

    return <pre className={ styles.eventPre }>{ JSON.stringify( event.detail, null, 2 ) }</pre>;
  };

  const depth = event.depth ?? 0;
  return (
    <div
      className={ styles.eventCard }
      style={ {
        borderLeftColor: kindAccents[ event.kind ] || '#6b7280',
        marginLeft: depth > 0 ? `${ depth * 16 }px` : undefined
      } }
    >
      <button type="button" className={ styles.eventHeader } onClick={ () => setExpanded( !expanded ) }>
        <span className={ styles.eventIcon } style={ { backgroundColor: kindAccents[ event.kind ] || '#6b7280' } }>
          {kindIcons[ event.kind ] || '?'}
        </span>
        <span className={ styles.eventTitle }>{event.title}</span>
        <span className={ styles.eventTime }>
          {formatISODateTime( event.timestamp, useLocalZone, 'hh:mm:ss.SSS a' )}
          {event.endTimestamp && ` (${ durationSeconds( event.timestamp, event.endTimestamp ) })`}
        </span>
        <span className={ styles.eventChevron }>
          {expanded ? <FiChevronDown /> : <FiChevronRight />}
        </span>
      </button>
      {expanded && (
        <div className={ styles.eventBody }>
          {renderDetail()}
        </div>
      )}
    </div>
  );
}

// ── LLM Message renderer ─────────────────────────────────────────────────────

function renderLLMMessages( messages: LLMMessage[] | string ) {
  if ( typeof messages === 'string' ) {
    try {
      const parsed = JSON.parse( messages );
      if ( Array.isArray( parsed ) ) return renderLLMMessages( parsed );
    } catch {
      return <pre className={ styles.eventPre }>{messages}</pre>;
    }
    return <pre className={ styles.eventPre }>{messages}</pre>;
  }

  if ( !Array.isArray( messages ) ) return <pre className={ styles.eventPre }>{JSON.stringify( messages, null, 2 )}</pre>;

  return (
    <div className={ styles.messagesContainer }>
      {messages.map( ( msg, i ) => (
        <div key={ i } className={ styles.messageItem }>
          <span className={ styles.messageRole }>{msg.role}</span>
          <pre className={ styles.messageContent }>
            {typeof msg.content === 'string'
              ? ( msg.content.length > 500 ? msg.content.slice( 0, 500 ) + '...' : msg.content )
              : JSON.stringify( msg.content, null, 2 )}
          </pre>
        </div>
      ) )}
    </div>
  );
}

// ── Export ────────────────────────────────────────────────────────────────────

interface AgentTraceViewerProps {
  agentId: string;
  useLocalZone: boolean;
  initialUrlState?: TraceUrlState;
  onUrlStateChange?: ( state: TraceUrlState ) => void;
  hideFiltersPanel?: boolean;
}

export function AgentTraceViewer( { agentId, useLocalZone, initialUrlState, onUrlStateChange, hideFiltersPanel }: AgentTraceViewerProps ) {
  return (
    <RequestListPanel
      agentId={ agentId }
      useLocalZone={ useLocalZone }
      initialUrlState={ initialUrlState }
      onUrlStateChange={ onUrlStateChange }
      hideFiltersPanel={ hideFiltersPanel }
    />
  );
}
